import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { API_BASE } from "../../utils/apiBase";

export default function LeaveRequestForm({ onSubmitted, enabled = true, onMetaLoaded }) {
  const [leaveForm, setLeaveForm] = useState({
    leave_type: "",
    start_date: "",
    end_date: "",
    reason: "",
    attachment: null,
  });
  const [allowedLeaveTypes, setAllowedLeaveTypes] = useState([]);
  const [allowedTypesLoading, setAllowedTypesLoading] = useState(false);
  const [allowedTypesError, setAllowedTypesError] = useState("");
  const [leaveDateError, setLeaveDateError] = useState("");
  const [submitMessage, setSubmitMessage] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [submitWarnings, setSubmitWarnings] = useState([]);

  const LEAVE_TYPE_CONFIG = {
    SICK: { code: "SICK", attachmentRequired: false, attachmentRecommended: true, followupAllowed: true, followupDeadlineDays: 5 },
    BEREAVEMENT: { code: "BEREAVEMENT", attachmentRequired: true, attachmentRecommended: false, followupAllowed: true, followupDeadlineDays: 10 },
    PATERNITY: { code: "PATERNITY", attachmentRequired: true, attachmentRecommended: false, followupAllowed: true, followupDeadlineDays: 30 },
    MATERNITY: { code: "MATERNITY", attachmentRequired: true, attachmentRecommended: false, followupAllowed: true, followupDeadlineDays: 60 },
    MATERNITY_EXT: { code: "MATERNITY_EXT", attachmentRequired: true, attachmentRecommended: false, followupAllowed: true, followupDeadlineDays: 5 },
    LWOP: { code: "LWOP", attachmentRequired: false, attachmentRecommended: false, followupAllowed: false, followupDeadlineDays: null },
    SIL: { code: "SIL", attachmentRequired: false, attachmentRecommended: false, followupAllowed: false, followupDeadlineDays: null },
    PERSONAL_LEAVE: { code: "SIL", attachmentRequired: false, attachmentRecommended: false, followupAllowed: false, followupDeadlineDays: null },
    VACATION: { code: "VACATION", attachmentRequired: false, attachmentRecommended: false, followupAllowed: false, followupDeadlineDays: null },
    VAWC: { code: "VAWC", attachmentRequired: true, attachmentRecommended: false, followupAllowed: true, followupDeadlineDays: 15 },
    CALAMITY: { code: "CALAMITY", attachmentRequired: false, attachmentRecommended: true, followupAllowed: true, followupDeadlineDays: 10 },
    SOLO_PARENT: { code: "SOLO_PARENT", attachmentRequired: true, attachmentRecommended: false, followupAllowed: true, followupDeadlineDays: 30 },
    SPECIAL_WOMEN: { code: "SPECIAL_WOMEN", attachmentRequired: true, attachmentRecommended: false, followupAllowed: true, followupDeadlineDays: 30 },
  };

  const normalizeLeaveTypeKey = (value) => {
    if (!value) return null;
    const raw = String(value).toLowerCase().replace(/[^a-z0-9]+/g, "_");
    const map = {
      sick: "SICK",
      sick_leave: "SICK",
      bereavement: "BEREAVEMENT",
      bereavement_leave: "BEREAVEMENT",
      paternity: "PATERNITY",
      paternity_leave: "PATERNITY",
      maternity: "MATERNITY",
      maternity_leave: "MATERNITY",
      maternity_ext: "MATERNITY_EXT",
      maternity_extension: "MATERNITY_EXT",
      maternity_leave_extension: "MATERNITY_EXT",
      maternity_leave_extension_unpaid: "MATERNITY_EXT",
      lwop: "LWOP",
      leave_without_pay: "LWOP",
      unpaid_leave: "LWOP",
      sil: "SIL",
      service_incentive: "SIL",
      service_incentive_leave: "SIL",
      personal: "PERSONAL_LEAVE",
      personal_leave: "PERSONAL_LEAVE",
      vacation: "VACATION",
      vacation_leave: "VACATION",
      vawc: "VAWC",
      calamity: "CALAMITY",
      calamity_leave: "CALAMITY",
      solo_parent: "SOLO_PARENT",
      solo_parent_leave: "SOLO_PARENT",
      special_women: "SPECIAL_WOMEN",
      special_leave_for_women: "SPECIAL_WOMEN",
    };
    return map[raw] || null;
  };

  const parseIsoDate = (iso) => {
    if (!iso) return null;
    const parts = String(iso).split("-").map(Number);
    if (parts.length !== 3) return null;
    const [y, m, d] = parts;
    if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
    return new Date(y, m - 1, d);
  };

  const formatIsoDate = (d) => {
    if (!d) return "";
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const addCalendarDaysIso = (startIso, daysToAdd) => {
    const start = parseIsoDate(startIso);
    if (!start) return "";
    const end = new Date(start.getTime());
    end.setDate(end.getDate() + Number(daysToAdd || 0));
    return formatIsoDate(end);
  };

  const isImmediateLeaveKey = (leaveKey) => leaveKey === "SICK" || leaveKey === "BEREAVEMENT" || leaveKey === "CALAMITY";

  const getManilaTodayISO = () => {
    try {
      const fmt = new Intl.DateTimeFormat("en-CA", {
        timeZone: "Asia/Manila",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
      return fmt.format(new Date());
    } catch (e) {
      return new Date().toISOString().split("T")[0];
    }
  };

  const manilaTodayISO = useMemo(() => getManilaTodayISO(), []);
  const manilaTomorrowISO = addCalendarDaysIso(manilaTodayISO, 1);
  const vacationEarliestISO = addCalendarDaysIso(manilaTodayISO, 7);
  const vacationEarliestDate = useMemo(() => parseIsoDate(vacationEarliestISO), [vacationEarliestISO]);
  const manilaTodayDate = useMemo(() => parseIsoDate(manilaTodayISO), [manilaTodayISO]);
  const immediateStartDateBounds = {
    minDate: parseIsoDate(manilaTodayISO),
    maxDate: parseIsoDate(manilaTomorrowISO),
  };

  const diffDays = (a, b) => {
    const ms = 24 * 60 * 60 * 1000;
    return Math.floor((a.getTime() - b.getTime()) / ms);
  };

  const validateAttachmentFile = (file) => {
    if (!file) return "";
    const maxBytes = 10 * 1024 * 1024;
    const name = file.name || "";
    const ext = name.slice(name.lastIndexOf(".")).toLowerCase();
    const allowed = [".png", ".jpg", ".jpeg", ".pdf"];
    if (!allowed.includes(ext) || file.size > maxBytes) {
      return "Attachment must be PNG, JPG, or PDF and not larger than 10MB.";
    }
    return "";
  };

  const validateLeaveRequestClient = ({ leaveType, startIso, endIso, hasFile }) => {
    const errors = [];
    const warnings = [];
    const leaveKey = normalizeLeaveTypeKey(leaveType);
    if (!leaveKey || !LEAVE_TYPE_CONFIG[leaveKey]) {
      errors.push("Unknown leave type.");
      return { errors, warnings };
    }
    const config = LEAVE_TYPE_CONFIG[leaveKey];
    const s = parseIsoDate(startIso);
    const e = parseIsoDate(endIso);
    if (!s || !e) {
      errors.push("Invalid start date or end date.");
      return { errors, warnings };
    }
    if (e.getTime() < s.getTime()) {
      errors.push("End date must be on or after start date.");
      return { errors, warnings };
    }

    const duration = diffDays(e, s) + 1;
    const diffFromToday = diffDays(manilaTodayDate || new Date(), s);

    switch (leaveKey) {
      case "SICK":
        if (duration > 4) errors.push("Sick Leave max is 4 consecutive days.");
        if (!(diffFromToday === 0 || diffFromToday === -1)) {
          errors.push("Sick Leave can only start today or tomorrow.");
        }
        break;
      case "BEREAVEMENT":
        if (duration > 7) errors.push("Bereavement Leave is fixed to 7 calendar days.");
        if (!(diffFromToday === 0 || diffFromToday === -1)) {
          errors.push("Bereavement Leave can only start today or tomorrow.");
        }
        break;
      case "PATERNITY":
        if (duration > 7) errors.push("Paternity Leave max is 7 calendar days.");
        if (diffFromToday > 0) errors.push("Paternity Leave cannot be filed for past dates.");
        break;
      case "MATERNITY":
        if (duration > 120) errors.push("Maternity Leave max is 120 calendar days.");
        if (diffFromToday > 0) errors.push("Maternity Leave cannot be filed for past dates.");
        break;
      case "MATERNITY_EXT":
        if (duration > 30) errors.push("Maternity Leave Extension (Unpaid) max is 30 calendar days.");
        if (diffFromToday > 0) errors.push("Maternity Leave Extension cannot be filed for past dates.");
        break;
      case "VAWC":
        if (duration > 10) errors.push("VAWC Leave max is 10 calendar days.");
        if (diffFromToday > 0) errors.push("VAWC Leave cannot be filed for past dates.");
        break;
      case "CALAMITY":
        if (duration > 5) errors.push("Calamity Leave max is 5 calendar days.");
        if (!(diffFromToday === 0 || diffFromToday === -1)) {
          errors.push("Calamity Leave can only start today or tomorrow.");
        }
        break;
      case "SOLO_PARENT":
        if (duration > 7) errors.push("Solo Parent Leave max is 7 calendar days.");
        if (diffFromToday > 0) errors.push("Solo Parent Leave cannot be filed for past dates.");
        break;
      case "SPECIAL_WOMEN":
        if (duration > 60) errors.push("Special Leave for Women max is 60 calendar days.");
        if (diffFromToday > 0) errors.push("Special Leave for Women cannot be filed for past dates.");
        break;
      case "LWOP":
        if (diffFromToday > 0) errors.push("Leave Without Pay cannot be filed for past dates.");
        break;
      case "VACATION":
        if (diffFromToday > 0) errors.push("Vacation Leave cannot be filed for past dates.");
        if (diffFromToday > -7) errors.push("Vacation Leave must be filed at least 7 days in advance.");
        break;
      case "SIL":
      case "PERSONAL_LEAVE":
        if (diffFromToday > 0) errors.push("This leave type cannot be filed for past dates.");
        break;
      default:
        break;
    }

    if (config.attachmentRequired && !hasFile) {
      warnings.push("This leave type requires an attachment (you may upload it later).");
    }
    if (config.attachmentRecommended && !hasFile) {
      warnings.push("It is recommended to attach a medical certificate for this leave.");
    }

    return { errors, warnings };
  };

  const handleLeaveTypeChange = (e) => {
    const type = e.target.value;
    const leaveKey = normalizeLeaveTypeKey(type);
    setLeaveDateError("");
    if (leaveKey === "BEREAVEMENT") {
      const startIso = manilaTodayISO;
      setLeaveForm({ ...leaveForm, leave_type: type, start_date: startIso, end_date: addCalendarDaysIso(startIso, 6) });
      return;
    }
    setLeaveForm({ ...leaveForm, leave_type: type, start_date: "", end_date: "" });
  };

  const isWeekend = (dateVal) => {
    const d = dateVal ? new Date(dateVal) : null;
    if (!d || Number.isNaN(d.getTime())) return false;
    const day = d.getDay();
    return day === 0 || day === 6;
  };

  const shouldDisableStartDate = (dateVal) => {
    const leaveKey = normalizeLeaveTypeKey(leaveForm.leave_type);
    if (isImmediateLeaveKey(leaveKey)) {
      const iso = formatIsoDate(new Date(dateVal));
      return iso !== manilaTodayISO && iso !== manilaTomorrowISO;
    }
    if (leaveKey === "VACATION") {
      const iso = formatIsoDate(new Date(dateVal));
      return iso < vacationEarliestISO || isWeekend(dateVal);
    }
    return isWeekend(dateVal);
  };

  const shouldDisableEndDate = (dateVal) => {
    if (normalizeLeaveTypeKey(leaveForm.leave_type) === "BEREAVEMENT") return true;
    if (isWeekend(dateVal)) return true;
    if (!leaveForm.start_date) return false;
    const start = parseIsoDate(leaveForm.start_date);
    const end = dateVal ? new Date(dateVal) : null;
    if (!start || !end || Number.isNaN(end.getTime())) return false;
    if (end.getTime() < start.getTime()) return true;
    const leaveKey = normalizeLeaveTypeKey(leaveForm.leave_type);
    if (leaveKey === "MATERNITY") {
      const max = new Date(start.getTime());
      max.setDate(max.getDate() + 119);
      if (end.getTime() > max.getTime()) return true;
    }
    if (leaveKey === "MATERNITY_EXT") {
      const max = new Date(start.getTime());
      max.setDate(max.getDate() + 29);
      if (end.getTime() > max.getTime()) return true;
    }
    return false;
  };

  const handleStartDateChange = (newVal) => {
    if (!newVal) {
      setLeaveForm({ ...leaveForm, start_date: "" });
      setLeaveDateError("");
      return;
    }
    const iso = formatIsoDate(new Date(newVal));
    if (normalizeLeaveTypeKey(leaveForm.leave_type) === "BEREAVEMENT") {
      const endIso = addCalendarDaysIso(iso, 6);
      const res = validateLeaveRequestClient({
        leaveType: leaveForm.leave_type,
        startIso: iso,
        endIso,
        hasFile: Boolean(leaveForm.attachment),
      });
      setLeaveDateError(res.errors && res.errors.length > 0 ? res.errors[0] : "");
      setLeaveForm({ ...leaveForm, start_date: iso, end_date: endIso });
      return;
    }
    let endIso = leaveForm.end_date;
    if (endIso) {
      const s = parseIsoDate(iso);
      const en = parseIsoDate(endIso);
      if (s && en && en.getTime() < s.getTime()) endIso = iso;
    }
    const res = validateLeaveRequestClient({
      leaveType: leaveForm.leave_type,
      startIso: iso,
      endIso: endIso || iso,
      hasFile: Boolean(leaveForm.attachment),
    });
    setLeaveDateError(res.errors && res.errors.length > 0 ? res.errors[0] : "");
    setLeaveForm({ ...leaveForm, start_date: iso, end_date: endIso || "" });
  };

  const handleEndDateChange = (newVal) => {
    if (!newVal) {
      setLeaveForm({ ...leaveForm, end_date: "" });
      setLeaveDateError("");
      return;
    }
    const iso = formatIsoDate(new Date(newVal));
    let startIso = leaveForm.start_date || iso;
    const res = validateLeaveRequestClient({
      leaveType: leaveForm.leave_type,
      startIso,
      endIso: iso,
      hasFile: Boolean(leaveForm.attachment),
    });
    if (res.errors && res.errors.length > 0) {
      setLeaveDateError(res.errors[0]);
      return;
    }
    setLeaveDateError("");
    setLeaveForm({ ...leaveForm, start_date: startIso, end_date: iso });
  };

  const handleLeaveFormChange = (e) => {
    if (e.target.type === "file") {
      const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
      setLeaveForm({ ...leaveForm, attachment: file });
      return;
    }
    if (e.target.name === "leave_type") {
      handleLeaveTypeChange(e);
      return;
    }
    setLeaveForm({ ...leaveForm, [e.target.name]: e.target.value });
  };

  const handleSubmitLeave = async () => {
    if (!enabled) {
      setSubmitError("Leave filing is not available right now.");
      return;
    }
    if (allowedTypesLoading) {
      setSubmitError("Leave types are still loading. Please wait.");
      return;
    }
    if (allowedTypesError) {
      setSubmitError(allowedTypesError);
      return;
    }
    if (allowedLeaveTypes.length === 0) {
      setSubmitError("No leave types are currently available for your account.");
      return;
    }
    if (!leaveForm.leave_type || !leaveForm.start_date || !leaveForm.end_date) {
      setSubmitError("Please fill in leave type, start date, and end date.");
      return;
    }
    setSubmitWarnings([]);
    const attachErr = validateAttachmentFile(leaveForm.attachment);
    if (attachErr) {
      setSubmitError(attachErr);
      return;
    }
    const resCheck = validateLeaveRequestClient({
      leaveType: leaveForm.leave_type,
      startIso: leaveForm.start_date,
      endIso: leaveForm.end_date,
      hasFile: Boolean(leaveForm.attachment),
    });
    if (leaveDateError || (resCheck.errors && resCheck.errors.length > 0)) {
      setSubmitError(leaveDateError || resCheck.errors[0]);
      return;
    }
    if (resCheck.warnings && resCheck.warnings.length > 0) {
      setSubmitWarnings(resCheck.warnings);
    }
    try {
      setSubmitError("");
      setSubmitMessage("");
      let resp;
      if (leaveForm.attachment) {
        const fd = new FormData();
        fd.append("leave_type", leaveForm.leave_type);
        fd.append("start_date", leaveForm.start_date);
        fd.append("end_date", leaveForm.end_date);
        fd.append("reason", leaveForm.reason);
        fd.append("attachment", leaveForm.attachment);
        resp = await axios.post(`${API_BASE}/api/leave-requests`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
          withCredentials: true,
        });
      } else {
        resp = await axios.post(`${API_BASE}/api/leave-requests`, leaveForm, {
          withCredentials: true,
        });
      }
      if (resp?.data?.warnings) setSubmitWarnings(resp.data.warnings);
      const status = String(resp?.data?.status || "").trim().toLowerCase();
      const successMessage =
        resp?.data?.message ||
        (status === "pending_proof"
          ? "Leave request submitted. Follow-up proof is still required before approval."
          : "Leave request submitted successfully!");
      setSubmitMessage(successMessage);
      setLeaveForm({
        leave_type: "",
        start_date: "",
        end_date: "",
        reason: "",
        attachment: null,
      });
      setSubmitError("");
      await Promise.resolve(onSubmitted?.());
      try {
        window.dispatchEvent(new Event("refreshNotifications"));
      } catch (e) {}
    } catch (error) {
      const serverMsg =
        error?.response?.data?.reason ||
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        (Array.isArray(error?.response?.data?.errors) ? error.response.data.errors[0] : null) ||
        error?.message;
      setSubmitError(serverMsg || "Failed to submit leave request. Please try again.");
      console.error("submit leave error", error, "serverMsg=", serverMsg);
    }
  };

  useEffect(() => {
    let cancelled = false;
    if (!enabled) {
      setAllowedLeaveTypes([]);
      setAllowedTypesLoading(false);
      setAllowedTypesError("");
      onMetaLoaded?.({ types: [], balances: {}, profile: null });
      return () => {
        cancelled = true;
      };
    }
    (async () => {
      setAllowedTypesLoading(true);
      setAllowedTypesError("");
      try {
        const res = await axios.get(`${API_BASE}/api/allowed-leave-types`, { withCredentials: true });
        let nextTypes = [];
        if (res.data) {
          if (Array.isArray(res.data.types)) {
            nextTypes = res.data.types;
          } else if (Array.isArray(res.data.allowed)) {
            nextTypes = res.data.allowed.map((l) => ({
              key: l.toLowerCase().replace(/[^a-z0-9]+/g, "_"),
              label: l,
              mode: "calendar",
              maxDays: null,
            }));
          } else if (Array.isArray(res.data)) {
            nextTypes = res.data;
          }
        }
        nextTypes = nextTypes.filter((type) => normalizeLeaveTypeKey(type?.key || type?.code || type?.label || type) !== "LWOP");
        if (cancelled) return;
        setAllowedLeaveTypes(nextTypes);
        onMetaLoaded?.({
          types: nextTypes,
          balances: res?.data?.balances || {},
          profile: res?.data?.profile || null,
        });
      } catch (e) {
        if (cancelled) return;
        setAllowedLeaveTypes([]);
        setAllowedTypesError("Failed to load allowed leave types.");
        onMetaLoaded?.({ types: [], balances: {}, profile: null, error: e });
      } finally {
        if (!cancelled) setAllowedTypesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, onMetaLoaded]);

  const activeLeaveKey = normalizeLeaveTypeKey(leaveForm.leave_type);
  const activeConfig = activeLeaveKey ? LEAVE_TYPE_CONFIG[activeLeaveKey] : null;
  const noAvailableTypes = enabled && !allowedTypesLoading && !allowedTypesError && allowedLeaveTypes.length === 0;
  const disableSubmit = !enabled || allowedTypesLoading || !!allowedTypesError || allowedLeaveTypes.length === 0;
  const leaveTypeHelperText = allowedTypesLoading
    ? "Loading leave types..."
    : allowedTypesError
      ? allowedTypesError
      : (noAvailableTypes ? "No leave types are currently available for your account." : "");

  return (
    <Paper
      className="bubble-card"
      sx={{
        p: { xs: 2, sm: 2.5, md: 3 },
        borderRadius: 3,
        mb: 3,
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}
    >
      <Typography variant="h6" gutterBottom>
        Submit Leave Request
      </Typography>
      <Box sx={{ mb: 2 }}>
        <TextField
          select
          label="Leave Type"
          name="leave_type"
          value={leaveForm.leave_type}
          onChange={handleLeaveFormChange}
          fullWidth
          disabled={disableSubmit}
          SelectProps={{ native: true }}
        >
          <option value=""></option>
          {(allowedLeaveTypes || []).map((lt) => (
            <option key={lt.key || lt.code || lt} value={lt.key || lt.code || lt}>
              {lt.label || lt}
            </option>
          ))}
        </TextField>
        {leaveTypeHelperText && (
          <Typography
            variant="caption"
            color={allowedTypesError ? "error" : "text.secondary"}
            sx={{ display: "block", mt: 0.75 }}
          >
            {leaveTypeHelperText}
          </Typography>
        )}
      </Box>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <DatePicker
          label="Start Date"
          value={leaveForm.start_date ? parseIsoDate(leaveForm.start_date) : null}
          onChange={handleStartDateChange}
          minDate={isImmediateLeaveKey(activeLeaveKey) ? immediateStartDateBounds.minDate : (activeLeaveKey === "VACATION" ? vacationEarliestDate : undefined)}
          maxDate={isImmediateLeaveKey(activeLeaveKey) ? immediateStartDateBounds.maxDate : undefined}
          shouldDisableDate={shouldDisableStartDate}
          slotProps={{
            textField: {
              fullWidth: true,
              sx: { mb: 2 },
              error: Boolean(leaveDateError),
              helperText: leaveDateError || (isImmediateLeaveKey(activeLeaveKey) ? "This leave can only start today or tomorrow." : (activeLeaveKey === "VACATION" ? "Vacation Leave must be filed at least 7 days in advance." : "")),
            },
          }}
        />
        <DatePicker
          label="End Date"
          value={leaveForm.end_date ? parseIsoDate(leaveForm.end_date) : null}
          onChange={handleEndDateChange}
          disabled={activeLeaveKey === "BEREAVEMENT"}
          shouldDisableDate={shouldDisableEndDate}
          slotProps={{
            textField: {
              fullWidth: true,
              sx: { mb: 2 },
              error: Boolean(leaveDateError),
              helperText: leaveDateError || (activeLeaveKey === "BEREAVEMENT" ? "Bereavement Leave is automatically set to 7 calendar days." : ""),
            },
          }}
        />
      </LocalizationProvider>
      <TextField
        label="Reason"
        name="reason"
        value={leaveForm.reason}
        onChange={handleLeaveFormChange}
        fullWidth
        multiline
        rows={3}
        sx={{ mb: 2 }}
      />
      <Box sx={{ mb: 2 }}>
        <input
          accept=".png,.jpg,.jpeg,.pdf"
          id="leave-attachment"
          type="file"
          name="attachment"
          onChange={handleLeaveFormChange}
          style={{ display: "block", width: "100%", maxWidth: "100%" }}
        />
        {leaveForm.attachment && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
            Selected: {leaveForm.attachment.name} ({Math.round(leaveForm.attachment.size / 1024)} KB)
          </Typography>
        )}
        {activeConfig?.attachmentRequired && (
          <Typography variant="caption" color="text.secondary">
            Attachment is required before approval for this leave type. You may upload it now or, if policy allows, as a follow-up.
          </Typography>
        )}
        {activeConfig?.attachmentRecommended && !activeConfig?.attachmentRequired && (
          <Typography variant="caption" color="text.secondary">
            Attachment is recommended for this leave type.
          </Typography>
        )}
      </Box>
      <Button
        variant="contained"
        color="primary"
        onClick={handleSubmitLeave}
        sx={{ mb: 2, width: { xs: '100%', sm: 'auto' } }}
        disabled={disableSubmit}
      >
        Submit Request
      </Button>
      {submitWarnings && submitWarnings.length > 0 && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {submitWarnings.map((w, i) => (
            <div key={i}>{w}</div>
          ))}
        </Alert>
      )}
      {submitMessage && <Alert severity="success">{submitMessage}</Alert>}
      {submitError && <Alert severity="error">{submitError}</Alert>}
    </Paper>
  );
}
